/**
 * A-GTCC-ROOT-BOUNDARY1 — the last line of defence for the whole admin app.
 *
 * `GtccViewBoundary` only wraps the active cockpit view. Measured in
 * `AdminRootBoundary.test.tsx`: a render throw anywhere else — the cockpit
 * shell, its Ask Genius panel, the admin header, a classic (gtcc1) page, the
 * session provider — unmounted the whole React root (React 19), and the
 * operator was left with a blank page and nothing to click.
 *
 * Mounted once in `main.tsx`: below the providers the fallback card itself
 * needs (theme, locale), above the session provider and the app it guards. A
 * throw inside a cockpit view still lands in the view boundary first; this
 * one only catches what nothing closer to the throw did.
 *
 * Semantics mirror `GtccViewBoundary`: `getDerivedStateFromError` for the
 * fallback, `componentDidCatch` logs (nothing is swallowed), and the
 * request-id of an `ApiError` so an operator can correlate with the access
 * log. The action is a full reload, not a state reset: at this height the
 * broken part may be a provider or the app itself, and re-rendering the same
 * tree would most likely throw again.
 */
import { Button, Card, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { Component, type ErrorInfo, type ReactNode } from "react";

import { ApiError } from "./lib/api.js";

interface AdminRootBoundaryProps {
  readonly children: ReactNode;
}

interface AdminRootBoundaryState {
  readonly error: Error | null;
}

function AdminRootErrorCard({ error }: { readonly error: Error }) {
  const { t } = useLocale();
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  return (
    <div style={{ padding: "var(--gt-sys-space-6)", maxInlineSize: "40rem", marginInline: "auto" }}>
      <Card elevation={1}>
        <Stack gap={3} role="alert" data-testid="admin-root-boundary-fallback">
          <h1 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg)" }}>
            {t("admin.root_error.title")}
          </h1>
          <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
            {t("admin.root_error.body")}
          </p>
          {requestId ? (
            <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
              {t("admin.root_error.request_id", { requestId })}
            </p>
          ) : null}
          <div>
            <Button
              variant="secondary"
              onClick={() => window.location.reload()}
              data-testid="admin-root-boundary-reload"
            >
              {t("admin.root_error.reload")}
            </Button>
          </div>
        </Stack>
      </Card>
    </div>
  );
}

export class AdminRootBoundary extends Component<AdminRootBoundaryProps, AdminRootBoundaryState> {
  override state: AdminRootBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AdminRootBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Same as GtccViewBoundary: straight to the console, where an operator can
    // capture stack and component trace. There is no client-side error channel
    // in apps/admin.
    console.error("AdminRootBoundary caught", { error, info });
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return <AdminRootErrorCard error={error} />;
  }
}
